import { useState, useMemo, useEffect } from 'react';
import api from '../api/axios';
import { MATERIAL_TABS, isFieldRequired } from '../pages/user/materialFieldConfig';
import { VENDOR_TABS } from '../pages/user/vendorFieldConfig';
import StatusBadge from './StatusBadge';
import { toast } from 'react-toastify';
import useVendorOptions from '../hooks/useVendorOptions';
import useMaterialOptions from '../hooks/useMaterialOptions';
import VendorFieldInput, { resolveFieldOptions } from './VendorFieldInput';

const TABS_BY_SOURCE = {
  material_requests: MATERIAL_TABS,
  vendor_requests: VENDOR_TABS,
};

const META = {
  material_requests: { gradient: 'linear-gradient(135deg,#D95A1A,#e87a3a)', label: 'Material Master Request' },
  vendor_requests:   { gradient: 'linear-gradient(135deg,#1A2C80,#2d3fa0)', label: 'Vendor Master Request' },
};

const CODE_COLUMNS = {
  material_requests: 'material_number',
  vendor_requests: 'business_partner_number',
};

const fmt = v => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const diffOf = (draft, request, keys) =>
  keys.reduce((acc, k) => {
    const from = request[k] === null || request[k] === undefined ? '' : String(request[k]);
    const to   = draft[k]   === null || draft[k]   === undefined ? '' : String(draft[k] ?? '');
    if (from !== to) acc[k] = draft[k] === '' ? null : draft[k];
    return acc;
  }, {});

const RequestFormModal = ({ request, onClose, editable = false, onSaved, actions }) => {
  const tabs = TABS_BY_SOURCE[request?.source];
  const [activeTab, setActiveTab] = useState(0);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [ifscStatus, setIfscStatus] = useState(null);

  const isVendor   = request?.source === 'vendor_requests';
  const isMaterial = request?.source === 'material_requests';

  // Material-type drives which fields are mandatory (mirrors the request form).
  const materialType = isMaterial ? String((draft.material_type ?? request?.material_type) || '') : '';

  const vendorOptions   = useVendorOptions(isVendor);
  const materialOptions = useMaterialOptions(isMaterial);

  // Re-fetch plant-dependent options when plant changes
  const plantVal = isMaterial ? String((draft.plant ?? request?.plant) || '') : '';
  useEffect(() => {
    if (!isMaterial) return;
    materialOptions.refetchPlantDependents(plantVal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantVal, isMaterial]);

  // Re-fetch sales-area-dependent options when sales org changes
  const salesOrgVal = isMaterial ? String((draft.sales_organization ?? request?.sales_organization) || '') : '';
  useEffect(() => {
    if (!isMaterial) return;
    materialOptions.refetchSalesAreaDependents(salesOrgVal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesOrgVal, isMaterial]);

  // IFSC auto-fill (vendor)
  const ifscValue = isVendor ? String((draft.bank_key_ifsc ?? request?.bank_key_ifsc) || '') : '';
  useEffect(() => {
    if (!isVendor || !ifscValue) { setIfscStatus(null); return; }
    const found = vendorOptions.bankKeys.find(b => b.code === ifscValue);
    if (found) {
      setDraft(prev => ({ ...prev, bank_id: found.description, bank_country_region_key: found.country || 'IN' }));
      setIfscStatus('ok');
    } else { setIfscStatus(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ifscValue, vendorOptions.bankKeys, isVendor]);

  // Re-fetch planning groups when company_code changes (vendor)
  const companyCodeVal = isVendor ? String((draft.company_code ?? request?.company_code) || '') : '';
  useEffect(() => {
    if (!isVendor) return;
    vendorOptions.refetchPlanningGroups(companyCodeVal);
    if (draft.company_code !== undefined) setDraft(prev => ({ ...prev, planning_group: '' }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCodeVal, isVendor]);

  const meta = META[request?.source] || { gradient: 'linear-gradient(135deg,#060d3a,#0d1a6e)', label: 'Request' };
  const codeColumn = CODE_COLUMNS[request?.source];
  const allKeys = useMemo(() => tabs ? tabs.flatMap(t => t.fields.map(f => f.key)) : [], [tabs]);
  const changes = useMemo(() => request ? diffOf(draft, request, allKeys) : {}, [draft, request, allKeys]);
  const tabStats = useMemo(() => tabs ? tabs.map(t => ({
    filled: t.fields.filter(f => fmt(request[f.key]) !== null).length,
    touched: t.fields.filter(f => f.key in draft).length,
    changed: t.fields.filter(f => f.key in changes).length,
  })) : [], [tabs, request, draft, changes]);

  if (!request || !tabs) return null;

  const activeFields = tabs[activeTab]?.fields || [];
  const changeCount = Object.keys(changes).length;
  const valueOf = key => (key in draft ? draft[key] : request[key]) ?? '';

  const approvers = [
    ...(request.requested_approver_name
      ? [{ name: request.requested_approver_name, email: request.requested_approver_email }]
      : []),
    ...(Array.isArray(request.additional_approvers) ? request.additional_approvers : []),
  ];

  const handleSave = async () => {
    if (!changeCount) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/requests/${request.id}/corrections`, {
        source: request.source,
        changes,
        comment: note.trim() || undefined,
      });
      toast.success(`Saved ${changeCount} correction${changeCount === 1 ? '' : 's'}`);
      setDraft({});
      setNote('');
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save corrections');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        {/* Header */}
        <div style={{ ...S.banner, background: meta.gradient }}>
          <div style={S.bannerCircle} />
          <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
            <div style={S.bannerTitle}>{meta.label}</div>
            <div style={S.bannerSub}>{request.request_number} · Submitted by {request.requester_name}</div>
          </div>
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
            <StatusBadge status={request.status} />
            {editable && changeCount > 0 && (
              <span style={S.changePill}>{changeCount} unsaved</span>
            )}
            <button onClick={onClose} style={S.closeBtn}>✕</button>
          </div>
        </div>

        <div style={S.body}>
          {/* Request info */}
          <div style={S.card}>
            <div style={S.cardHeader}>Request Information</div>
            <div style={S.infoGrid}>
              <div style={S.infoField}>
                <span style={S.infoLabel}>Title</span>
                <span style={S.infoVal}>{request.title}</span>
              </div>
              <div style={S.infoField}>
                <span style={S.infoLabel}>Submitted</span>
                <span style={S.infoVal}>{new Date(request.created_at).toLocaleString()}</span>
              </div>
              {request.description && (
                <div style={{ ...S.infoField, gridColumn: '1 / -1' }}>
                  <span style={S.infoLabel}>Description / Justification</span>
                  <span style={S.infoVal}>{request.description}</span>
                </div>
              )}
            </div>
          </div>

          {/* Approvers */}
          {approvers.length > 0 && (
            <div style={S.card}>
              <div style={S.cardHeader}>Approver Details</div>
              <div style={S.approverGrid}>
                {approvers.map((a, i) => (
                  <div key={i} style={S.approverRow}>
                    <span style={S.approverIndex}>{i + 1}</span>
                    <div>
                      <div style={S.approverName}>{a.name}</div>
                      <div style={S.approverEmail}>{a.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Remarks */}
          {(request.approver_remarks || request.it_remarks) && (
            <div style={S.card}>
              <div style={S.cardHeader}>Remarks</div>
              {request.approver_remarks && (
                <div style={S.remarkBox}>
                  <span style={S.remarkLabel}>Approver Remarks</span>
                  {request.approver_remarks}
                </div>
              )}
              {request.it_remarks && (
                <div style={{ ...S.remarkBox, background: '#fff7ed', borderColor: '#fed7aa' }}>
                  <span style={{ ...S.remarkLabel, color: '#c2410c' }}>IT Remarks</span>
                  {request.it_remarks}
                </div>
              )}
            </div>
          )}

          {/* Field data card */}
          <div style={S.card}>
            <div style={S.cardHeaderRow}>
              <span style={S.cardHeaderInline}>{meta.label.replace(' Request', '')} Details</span>
              <span style={S.counter}>
                {tabs.flatMap(t => t.fields).filter(f => fmt(request[f.key]) !== null).length} / {tabs.flatMap(t => t.fields).length} fields filled
                {editable && changeCount > 0 && <span style={S.counterChanged}> · {changeCount} edited</span>}
              </span>
            </div>

            {editable && (
              <div style={S.editHint}>
                {isMaterial ? (
                  <>✎ Edit any field and save. Fields marked <span style={S.req}>*</span> are mandatory for the selected material type.</>
                ) : (
                  <>✎ Edit any field and save. The system-generated <strong>{codeColumn?.replace(/_/g, ' ')}</strong> is IT-only and cannot be changed here.</>
                )}
              </div>
            )}

            <div style={S.tabBar}>
              {tabs.map((t, i) => {
                const { filled, changed } = tabStats[i];
                return (
                  <button key={t.id} type="button" onClick={() => setActiveTab(i)}
                    style={{ ...S.tab, ...(i === activeTab ? { ...S.tabActive, boxShadow: `inset 0 -3px 0 ${meta.gradient}` } : null) }}>
                    {t.label}
                    {filled > 0 && <span style={S.tabCount}>{filled}</span>}
                    {changed > 0 && <span style={S.tabChanged}>{changed}*</span>}
                  </button>
                );
              })}
            </div>

            <div style={S.fieldGrid}>
              {activeFields.map(f => {
                const locked = f.key === codeColumn && !isMaterial;
                const val = fmt(request[f.key]);
                const changed = f.key in changes;
                const fieldValue = valueOf(f.key);
                const fieldOptions = resolveFieldOptions(f.key, request.source, vendorOptions, materialOptions);
                return (
                  <div key={f.key} style={S.field}>
                    <label style={S.label}>
                      {f.label}
                      {isMaterial
                        ? (!locked && isFieldRequired(f, materialType)) && <span style={S.req}> *</span>
                        : f.required && <span style={S.req}> *</span>}
                      {locked && <span style={S.lockTag}>IT only</span>}
                      {changed && <span style={S.changedTag}>edited</span>}
                    </label>
                    {locked ? (
                      <div style={{ ...S.valueBox, color: val ? '#0B1554' : '#94a3b8', ...S.valueBoxLocked }}>
                        {!val ? 'Filled in by IT at completion' : val}
                      </div>
                    ) : (
                      <div style={editable && changed ? { outline: '2px solid #f59e0b', borderRadius: 8 } : {}}>
                        <VendorFieldInput
                          field={f}
                          value={fieldValue}
                          onChange={v => setDraft(prev => ({ ...prev, [f.key]: v }))}
                          options={fieldOptions}
                          ifscStatus={ifscStatus}
                          readOnly={!editable}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Save corrections bar */}
            {editable && changeCount > 0 && (
              <div style={S.saveBar}>
                <div style={S.saveBarLeft}>
                  <span style={S.saveCount}>{changeCount} unsaved correction{changeCount === 1 ? '' : 's'}</span>
                  <input
                    style={S.noteInput}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Optional note posted to the discussion thread…"
                  />
                </div>
                <div style={S.saveBarRight}>
                  <button style={S.btnDiscard} onClick={() => { setDraft({}); setNote(''); }}>Discard</button>
                  <button style={{ ...S.btnSave, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save corrections'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {actions && <div style={S.actionBar}>{actions}</div>}
        </div>
      </div>
    </div>
  );
};

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(6,13,58,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' },
  modal: { background: '#f4f6fb', borderRadius: 18, width: '100%', maxWidth: 900, boxShadow: '0 24px 64px rgba(6,13,58,0.25)', overflow: 'hidden' },
  banner: { padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 16, position: 'relative', overflow: 'hidden' },
  bannerCircle: { position: 'absolute', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: -70, right: -40 },
  bannerTitle: { color: '#fff', fontSize: 18, fontWeight: 800, marginBottom: 4 },
  bannerSub: { color: 'rgba(255,255,255,0.7)', fontSize: 12.5 },
  closeBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  changePill: { background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 20 },

  body: { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: '#fff', borderRadius: 14, padding: '18px 22px', boxShadow: '0 2px 12px rgba(11,21,84,0.05)' },
  cardHeader: { fontSize: 11, fontWeight: 800, color: '#0B1554', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f0f2f8' },
  cardHeaderRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f0f2f8' },
  cardHeaderInline: { fontSize: 11, fontWeight: 800, color: '#0B1554', letterSpacing: 0.8, textTransform: 'uppercase' },
  counter: { fontSize: 12, fontWeight: 600, color: '#8892b0' },
  counterChanged: { color: '#b45309', fontWeight: 800 },

  editHint: { fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 14 },

  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' },
  infoField: { marginBottom: 10 },
  infoLabel: { display: 'block', fontSize: 10.5, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 3 },
  infoVal: { fontSize: 13.5, color: '#0B1554', fontWeight: 500 },

  approverGrid: { display: 'flex', flexDirection: 'column', gap: 8 },
  approverRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: '#f8faff', borderRadius: 10, border: '1px solid #eef1f7' },
  approverIndex: { width: 22, height: 22, borderRadius: '50%', background: '#0B1554', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  approverName: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  approverEmail: { fontSize: 12, color: '#4f6ef7' },

  remarkBox: { fontSize: 13, color: '#374151', background: '#f8faff', border: '1px solid #e5e9f5', borderRadius: 10, padding: '10px 12px', marginBottom: 8, whiteSpace: 'pre-wrap', lineHeight: 1.6 },
  remarkLabel: { display: 'block', fontSize: 10, fontWeight: 800, color: '#4f6ef7', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 },

  tabBar: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 18, borderBottom: '1px solid #eef1f7' },
  tab: { position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '9px 14px', fontSize: 12.5, fontWeight: 700, color: '#8892b0', borderRadius: '8px 8px 0 0', whiteSpace: 'nowrap' },
  tabActive: { color: '#0B1554', background: '#f8faff' },
  tabCount: { display: 'inline-block', minWidth: 16, padding: '0 5px', marginLeft: 6, borderRadius: 8, background: '#eef1f7', color: '#8892b0', fontSize: 10.5, fontWeight: 700, textAlign: 'center', verticalAlign: 'middle' },
  tabChanged: { display: 'inline-block', minWidth: 16, padding: '0 5px', marginLeft: 4, borderRadius: 8, background: '#fef3c7', color: '#b45309', fontSize: 10.5, fontWeight: 800, textAlign: 'center', verticalAlign: 'middle' },

  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' },
  field: { marginBottom: 14 },
  label: { display: 'block', fontSize: 11.5, fontWeight: 600, color: '#4a5568', marginBottom: 5 },
  req: { color: '#DD3222' },
  lockTag: { marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#64748b', background: '#eef1f7', padding: '1px 5px', borderRadius: 4 },
  changedTag: { marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '1px 5px', borderRadius: 4 },

  input: { width: '100%', padding: '9px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box' },
  valueBox: { width: '100%', padding: '9px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, background: '#f8faff', minHeight: 38, boxSizing: 'border-box' },
  valueBoxLocked: { background: '#f1f5f9', color: '#94a3b8', borderColor: '#e2e8f0' },

  saveBar: { marginTop: 18, padding: '14px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' },
  saveBarLeft: { flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 8 },
  saveBarRight: { display: 'flex', gap: 8, flexShrink: 0 },
  saveCount: { fontSize: 12, fontWeight: 800, color: '#b45309' },
  noteInput: { width: '100%', padding: '9px 11px', border: '1.5px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#0B1554', background: '#fff', boxSizing: 'border-box' },
  btnDiscard: { padding: '10px 18px', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#fff' },
  btnSave: { padding: '10px 22px', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg,#0d1a6e,#4f6ef7)', boxShadow: '0 4px 12px rgba(79,110,247,0.35)' },

  actionBar: { display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#fff', borderRadius: 14, padding: '14px 18px', boxShadow: '0 2px 12px rgba(11,21,84,0.05)' },
  actionBtn: { padding: '11px 26px', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
};

export default RequestFormModal;
